// src/pages/Community/News_Updates/Article.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  increment,
  updateDoc,
  onSnapshot,
  collection,
  addDoc,
  setDoc,
} from "firebase/firestore";
import { db, auth } from "../../../firebase";
import ReactMarkdown from "react-markdown";
import { formatDistanceToNow } from "date-fns";
import { Helmet } from "react-helmet";
import "../css/Article.css";

const Article = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [commentCount, setCommentCount] = useState(0);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [userMap, setUserMap] = useState({});
  const hasIncrementedView = useRef(false);
  const [userReaction, setUserReaction] = useState({});

  useEffect(() => {
    const articleRef = doc(db, "articles", id);

    const unsubscribe = onSnapshot(
      articleRef,
      async (docSnap) => {
        if (!docSnap.exists()) {
          console.warn("[Article] ❗ Article not found");
          return;
        }

        const data = docSnap.data();

        const words = data.content?.trim().split(/\s+/).length || 0;
        const readingTime = Math.ceil(words / 200);

        setArticle({ id: docSnap.id, ...data, readingTime });

        if (!hasIncrementedView.current) {
          hasIncrementedView.current = true;
          try {
            await updateDoc(articleRef, { views: increment(1) });
          } catch (error) {
            console.error("[Article] ❌ Failed to increment views:", error.message);
          }
        }
      },
      (error) => {
        console.error("[Article] ❌ onSnapshot for article failed:", error.message);
      }
    );

    return () => unsubscribe();
  }, [id]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "articles", id, "comments"),
      async (snapshot) => {
        const commentList = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => b.timestamp?.toDate() - a.timestamp?.toDate());

        setCommentCount(commentList.length);
        setComments(commentList);

        const uniqueUserIds = [...new Set(commentList.map((c) => c.userId))];
        const userMapTemp = {};

        const userPromises = uniqueUserIds.map(async (uid) => {
          try {
            const userDoc = await getDoc(doc(db, "public_profiles", uid));
            if (userDoc.exists()) {
              const { photoURL, displayName } = userDoc.data();
              userMapTemp[uid] = { photoURL, displayName };
            }
          } catch (error) {
            console.error(`[Article] ❌ Error fetching public profile for ${uid}:`, error.message);
          }
        });

        await Promise.all(userPromises);
        setUserMap(userMapTemp);
      },
      (error) => {
        console.error("[Article] ❌ onSnapshot for comments failed:", error.message);
      }
    );

    return () => unsubscribe();
  }, [id]);

  useEffect(() => {
    if (!auth.currentUser) return;

    const reactionRef = doc(db, "articles", id, "reactions", auth.currentUser.uid);
    const unsubscribe = onSnapshot(
      reactionRef,
      (snap) => {
        if (snap.exists()) setUserReaction(snap.data());
        else setUserReaction({});
      },
      (error) => {
        console.error("[Article] ❌ onSnapshot for user reactions failed:", error.message);
      }
    );

    return () => unsubscribe();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    try {
      const commentRef = collection(db, "articles", id, "comments");
      const articleRef = doc(db, "articles", id);

      await addDoc(commentRef, {
        comment,
        userId: auth.currentUser.uid,
        timestamp: new Date(),
      });

      await updateDoc(articleRef, {
        "reactions.comment": increment(1),
      });

      setComment("");
    } catch (error) {
      console.error("[Article] ❌ Failed to submit comment:", error.message);
    }
  };

  const handleReact = async (type) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    try {
      const reactionRef = doc(db, "articles", id, "reactions", userId);
      const articleRef = doc(db, "articles", id);

      const reactionSnap = await getDoc(reactionRef);

      if (reactionSnap.exists()) {
        const current = reactionSnap.data()[type];
        await updateDoc(reactionRef, { [type]: !current });
        await updateDoc(articleRef, { [`reactions.${type}`]: increment(current ? -1 : 1) });
      } else {
        await setDoc(reactionRef, { [type]: true });
        await updateDoc(articleRef, { [`reactions.${type}`]: increment(1) });
      }
    } catch (error) {
      console.error(`[Article] ❌ Failed to react (${type}):`, error.message);
    }
  };

  if (!article) return <p>Loading...</p>;

  // SEO block left unchanged (you may want to update canonical to community path)
  const seo = (() => {
    const title = article?.metaTitle || (article?.title ? `${article.title} | GTAFanPage.com` : "GTAFanPage.com – GTA 6 News");
    const description = article?.metaDescription || (article?.content ? article.content.replace(/[#>*`~_]/g, "").replace(/\n/g, " ").substring(0, 160) : "Explore exclusive GTA 6 content on GTAFanPage.com.");
    const url = `https://www.gtafanpage.com/community/news_updates/article/${article?.slug || id}`;
    const image = article?.ogImageUrl || "https://www.gtafanpage.com/og-image.jpg";
    const published = article?.timestamp?.toDate?.()?.toISOString?.();
    const updated = article?.updatedAt?.toDate?.()?.toISOString?.();
    const robots = article?.published === false ? "noindex, nofollow" : "index, follow";
    return { title, description, url, image, published, updated, robots };
  })();

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="robots" content={seo.robots} />
        <link rel="canonical" href={seo.url} />
        <meta name="description" content={seo.description} />
        {article?.tags?.length ? <meta name="keywords" content={article.tags.join(", ")} /> : null}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="GTAFanPage.com" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.url} />
        <meta property="og:image" content={seo.image} />
      </Helmet>

      <>
        <div className="article-page-grid full-width">
          <div className="article-detail">
            <div className="article-header">
              <h1 className="article-title">{article.title}</h1>
              <p className="article-meta">
                By {article.authorName || "Unknown"} · {article.category} ·{" "}
                {article.timestamp?.toDate().toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}{" "}
                · {article.readingTime} min read · {article.views || 0} views ·{" "}
                {commentCount} comments
              </p>
              <div className="article-tags">
                {article.tags?.map((tag) => (
                  <span key={tag} className="tag">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <hr className="divider" />

            <div className="article-content markdown-body">
              <ReactMarkdown>{article.content}</ReactMarkdown>
            </div>

            <div className="emoji-reactions">
              {Object.entries({ like: "👍", fire: "🔥" }).map(([type, emoji]) => {
                const active = userReaction?.[type];
                return (
                  <button key={type} onClick={() => handleReact(type)} className={`emoji-button ${active ? "active" : ""}`}>
                    {emoji} {article.reactions?.[type] || 0}
                  </button>
                );
              })}
              <span className="emoji-button comment-static">💬 {commentCount}</span>
            </div>

            <hr className="divider" />
            <p>💬 Leave your thoughts in the comments below!</p>

            <div className="comment-list">
              {comments.map((c) => {
                const user = userMap[c.userId];
                const avatar = user?.photoURL || "";
                const fullDate = c.timestamp?.toDate().toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "2-digit",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                });

                return (
                  <div key={c.id} className="comment-item">
                    <div className="comment-meta">
                      {avatar && <img src={avatar} alt="avatar" className="comment-avatar" />}
                      <div className="comment-info">
                        <div className="comment-header">
                          <strong>{user?.displayName || "Anonymous"}</strong>
                          <span className="comment-timestamp" title={fullDate}>
                            · {formatDistanceToNow(c.timestamp?.toDate(), { addSuffix: true })}
                          </span>
                        </div>
                        <div className="comment-text">{c.comment}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {auth.currentUser ? (
              <form onSubmit={handleSubmit} className="comment-form">
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." required />
                <button type="submit">Post Comment</button>
              </form>
            ) : (
              <p className="login-to-comment">Please log in to comment.</p>
            )}
          </div>
        </div>
      </>
    </>
  );
};

export default Article;
